import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  LocationOn as LocationIcon, 
  Phone as PhoneIcon, 
  Email as EmailIcon, 
  LocalOffer as TaxCodeIcon 
} from '@mui/icons-material';
import { companyService } from '../../services/companyService';
import taxService from '../../services/taxService';
import { useSnackbar } from 'notistack';
import { ErrorDisplay } from '../../components/common/ErrorDisplay';

const CompanyDetail = () => {
  // Route is /companies/:id; accept :companyId too for older links.
  const { id: routeId, companyId: legacyId } = useParams();
  const companyId = routeId || legacyId;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [company, setCompany] = useState(null);
  const [taxRegistrations, setTaxRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        
        // Fetch company details
        const companyData = await companyService.getCompany(companyId);
        setCompany(companyData);
        
        // Fetch tax registrations
        try {
          const cid = parseInt(companyId, 10);
          const registrations = await taxService.listSalesTaxRegistrations({ company_id: cid });
          // The API scopes by company_id; keep a defensive filter (rows carry company.id).
          const companyRegistrations = (Array.isArray(registrations) ? registrations : []).filter(
            reg => (reg.company?.id ?? reg.company_id ?? cid) === cid
          );
          setTaxRegistrations(companyRegistrations);
        } catch (regError) {
          logger.error('Error fetching tax registrations:', regError);
          // Not setting error here to still show company details
        }
        
        setError(null);
      } catch (err) {
        logger.error('Error loading company details:', err);
        setError('Failed to load company details. Please try again later.');
        enqueueSnackbar('Error loading company details', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompanyData();
  }, [companyId, enqueueSnackbar]);
  
  const handleEditTaxCode = () => {
    navigate(`/company/${companyId}/tax-codes`);
  };
  
  const handleEditCompany = () => {
    navigate(`/companies/${companyId}/edit`);
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !company) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <ErrorDisplay
          error={error}
          userMessage={error || 'Company not found'}
        />
        <Button 
          variant="contained" 
          onClick={() => navigate('/companies')}
          sx={{ mt: 2 }}
        >
          Back to Companies
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{company.name}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleEditCompany}
        >
          Edit Company
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Company Information" />
            <CardContent>
              <List>
                <ListItem>
                  <BusinessIcon sx={{ mr: 2 }} />
                  <ListItemText 
                    primary={company.name}
                    secondary="Company Name"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary={company.tax_id}
                    secondary="Tax ID"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <LocationIcon sx={{ mr: 2 }} />
                  <ListItemText 
                    primary={
                      <>
                        {company.address_line1}
                        {company.address_line2 && <div>{company.address_line2}</div>}
                        {company.city}, {company.state} {company.zip_code}
                        <div>{company.country}</div>
                        {company.is_head_office && (
                          <Box 
                            component="span"
                            sx={{
                              display: 'inline-block',
                              mt: 1,
                              px: 1.5,
                              py: 0.5,
                              bgcolor: 'primary.main',
                              color: 'white',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            HEAD OFFICE
                          </Box>
                        )}
                      </>
                    }
                    secondary="Address"
                  />
                </ListItem>
                {company.phone && (
                  <>
                    <Divider />
                    <ListItem>
                      <PhoneIcon sx={{ mr: 2 }} />
                      <ListItemText 
                        primary={company.phone}
                        secondary="Phone"
                      />
                    </ListItem>
                  </>
                )}
                {company.email && (
                  <>
                    <Divider />
                    <ListItem>
                      <EmailIcon sx={{ mr: 2 }} />
                      <ListItemText 
                        primary={company.email}
                        secondary="Email"
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Tax Information" 
              action={
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<TaxCodeIcon />}
                  onClick={handleEditTaxCode}
                >
                  Choose Product Tax Code
                </Button>
              }
            />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Tax Code</Typography>
                {company.tax_code ? (
                  <Chip 
                    label={company.tax_code} 
                    color="primary" 
                    variant="outlined" 
                    icon={<TaxCodeIcon />}
                    sx={{ fontSize: '1rem' }}
                  />
                ) : (
                  <Typography color="text.secondary">
                    No tax code set. Setting a tax code helps GoTruTax determine the correct tax treatment for this company's transactions.
                  </Typography>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Tax Registrations
                </Typography>
                {taxRegistrations.length > 0 ? (
                  <List disablePadding>
                    {taxRegistrations.map((reg, index) => (
                      <Paper key={index} variant="outlined" sx={{ mb: 1, p: 1 }}>
                        <Typography variant="body1">
                          {reg.state ? `${reg.state}, ${reg.country}` : reg.country}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Type: {reg.registration_type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: <Chip size="small" label={reg.status.toUpperCase()} 
                            color={reg.status === 'active' ? 'success' : 'default'} />
                        </Typography>
                      </Paper>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    No tax registrations found for this company.
                  </Typography>
                )}
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => navigate('/tax/registration')}
                  sx={{ mt: 2 }}
                >
                  Add Tax Registration
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box display="flex" justifyContent="flex-start" mt={3}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/companies')}
        >
          Back to Companies
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyDetail; 